import type { PaymentMethod } from '../components/PaymentMethodSwitch';
import { pixNaRenovacao, type RenewalPixMode } from './renewal-plans';

/** O que o checkout público lê do plano pra decidir as formas de pagamento. */
export interface PlanoDoCheckout {
  billingCycle?: string | null;
  planType?: string | null;
  /** PIX do ano inteiro no checkout público (plans.pix_a_vista). */
  pixAVista?: boolean | null;
  /** Venda bloqueada (plans.sale_blocked). */
  saleBlocked?: boolean | null;
}

/**
 * Plano à venda no checkout público. Venda bloqueada (`plans.sale_blocked`,
 * 22/10/2026 — os "Anual Recorrente") abre a tela de "plano indisponível" em
 * vez do formulário: o POST /payments recusaria no fim (`plano_indisponivel`).
 * Aqui é só a tela; quem recusa de verdade é o backend.
 */
export function planoVendavelNoCheckout(plano: PlanoDoCheckout | null | undefined): boolean {
  return plano?.saleBlocked !== true;
}

/**
 * PIX no checkout PÚBLICO, pelo plano:
 *   `mensal`        — plano mensal puro: PIX do mês.
 *   `anual_a_vista` — plano anual à vista COM a flag `pixAVista` (oferta
 *                     ligada no painel): PIX do valor TOTAL do ano, uma vez.
 *   null            — o resto (anual sem a flag, fidelidade, parcelado): só cartão.
 * É a régua da renovação (`pixNaRenovacao`) com o anual à vista atrás da flag
 * do plano. Aqui é só a tela; quem recusa de verdade é o POST /payments/pix.
 */
export function pixNoCheckout(plano: PlanoDoCheckout | null | undefined): RenewalPixMode | null {
  if (!plano) return null;
  const modo = pixNaRenovacao({
    billingCycle: plano.billingCycle === 'annual' ? 'annual' : 'monthly',
    planType: plano.planType ?? null,
  });
  if (modo === 'anual_a_vista') return plano.pixAVista === true ? modo : null;
  return modo;
}

/**
 * Formas que o plano oferece no checkout público. A aba do PIX e o #512
 * (`cupomSemFormaDisponivel`) leem esta MESMA lista — se divergirem, o cupom
 * só-PIX é recusado num plano que mostra o PIX (ou aceito num que não mostra).
 */
export function formasNoCheckout(plano: PlanoDoCheckout | null | undefined): PaymentMethod[] {
  return pixNoCheckout(plano) ? ['credit-card', 'pix'] : ['credit-card'];
}

/**
 * Valor do PIX no checkout público, já com o cupom: o mês no mensal, o ano
 * INTEIRO no à vista — nunca a parcela (÷12) que o cartão anual mostra.
 * `detalhe` é a linha sob o valor na barra do celular (mesma copy da
 * renovação). null = o plano não tem PIX.
 */
export function precoNoPix(
  plano: PlanoDoCheckout | null | undefined,
  precoComCupom: number,
): { valor: number; detalhe: string | null } | null {
  const modo = pixNoCheckout(plano);
  if (!modo) return null;
  return {
    valor: precoComCupom,
    detalhe: modo === 'anual_a_vista' ? 'à vista no PIX · vale por 12 meses' : null,
  };
}

/**
 * Selo do plano no resumo: "Nome - Anual". Nome que já diz o ciclo
 * ("Aventureiro Anual - Oferta Encontro") não repete o tipo no fim.
 */
export function rotuloDoPlano(nome: string, tipo: 'Anual' | 'Mensal'): string {
  return /\b(anual|mensal)\b/i.test(nome) ? nome : `${nome} - ${tipo}`;
}
